import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import axiosInstance from '../utils/axiosInstance';
import { plotlyStyle } from '../utils/plotlyStyle';
import {
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { MESSAGES } from '../constants/messages';
import AutocompleteInput from '../components/AutocompleteInput';

const ChartStockHistory = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [from, setFrom] = useState(
    dayjs().subtract(1, 'month').startOf('month')
  );

  const [to, setTo] = useState(
    dayjs().endOf('month')
  );

  const [skuKeyword, setSkuKeyword] =
    useState('');

  const [selectedVariant, setSelectedVariant] =
    useState(null);

  const fetchData = async (
    f,
    t,
    variantId = null
  ) => {
    setLoading(true);

    try {
      const params = {
        from: f.format('YYYY-MM-DD'),
        to: t.format('YYYY-MM-DD'),
      };

      if (variantId) {
        params.variantId = variantId;
      }

      const res = await axiosInstance.get(
        '/chart/stock-history',
        { params }
      );

      setData(res.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(from, to);
  }, []);

  if (loading && !data) {
    return (
      <Typography>
        {MESSAGES.LOADING}
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography>
        {error || MESSAGES.LOAD_FAILED}
      </Typography>
    );
  }

  const plotData = data.types.map(
    (type, idx) => ({
      x: data.dates,
      y: data.dataByType[type],
      type: 'scatter',
      mode: 'lines+markers',
      name: type,
      marker: {
        color:
          plotlyStyle.colors[
          idx %
          plotlyStyle.colors.length
          ],
      },
    })
  );

  return (
    <>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="ja"
      >
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <DatePicker
              label="開始日"
              value={from}
              onChange={setFrom}
              views={['year', 'month', 'day']}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />

            <Typography>〜</Typography>

            <DatePicker
              label="終了日"
              value={to}
              onChange={setTo}
              views={['year', 'month', 'day']}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"

          >
            <AutocompleteInput
              sx={{ width: 300 }}
              value={skuKeyword}
              index={0}
              label="品番検索"
              isEditMode={true}
              hasLimit={() => false}
              fetchSuggestions={async (
                keyword
              ) => {
                try {
                  const res =
                    await axiosInstance.post(
                      '/product/sku_suggest',
                      {
                        keyword,
                      }
                    );

                  return res.data;
                } catch (err) {
                  console.error(err);
                  return [];
                }
              }}
              onChange={(
                e,
                idx,
                option
              ) => {
                setSkuKeyword(
                  e?.target?.value || ''
                );

                setSelectedVariant(
                  option || null
                );
              }}

            />

            <Button
              size="small"
              variant="contained"
              onClick={() =>
                fetchData(
                  from,
                  to,
                  selectedVariant?.variant_id ||
                  null
                )
              }
            >
              適用
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setSkuKeyword('');
                setSelectedVariant(null);

                fetchData(from, to);
              }}
            >
              選択解除
            </Button>
          </Stack>
        </Stack>
      </LocalizationProvider>

      <div>
        <Plot
          data={plotData}
          layout={{
            ...plotlyStyle.layout,
            title: {
              text: selectedVariant
                ? `${selectedVariant.sku} の在庫推移`
                : '在庫トランザクションタイプ別推移',
            },
            xaxis: {
              ...plotlyStyle.layout
                .xaxis,
              title: '日付',
              type: 'date',
              tickformat: '%Y-%m-%d',
            },
            yaxis: {
              ...plotlyStyle.layout
                .yaxis,
              title: '数量',
            },
            legend: {
              orientation: 'h',
              x: 0,
              y: 1,
              xanchor: 'left',
              yanchor: 'bottom',
            },
            margin: {
              t: 80,
              b: 80,
              l: 60,
              r: 60,
            },
          }}
          style={{
            width: '100%',
            height: '100%',
            minHeight: 500,
          }}
          useResizeHandler={true}
        />
      </div>
    </>
  );
};

export default ChartStockHistory;